// Added by Sejune Oh on 2020-03-18
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { FC, useEffect, useState, useCallback } from 'react'

// Helper
import LegacyEvtHandler from '@/helper/LegacyEvtHandler'
import Logger from '@/helper/Logger'
import Intl from '@/helper/Intl'
import { downloadFile } from '@/helper/util'
import { Task } from '@/helper/define/taskDefine'

// API
import {
  reqGetLogs,
  reqGetLogByPath,
  reqCancelTaskById,
  reqRestartTaskById,
  reqKillTaskByIdWithProcName,
  getURLforDownloadTaskLogs,
  LogProps
} from '@/service/study'

import { reqGetAPSEngines } from '@/service/apsRule.service'

// UI
import { TaskDetailView } from '@/components/worklist/TaskDetailView'
import { TaskLogDialog, TaskLogsInfoProps } from '@/components/common/v2/Dialog/TaskLogDialog'
import { ConfirmDialog, ConfirmDialogProps } from '@/components/common/v2/Dialog/ConfirmDialog'

// Constants
import {
  EVT_TASKLIST_INIT,
  EVT_TASKLIST_SELECTION,
  EVT_UPDATE_CURRENT_TASK_STATUS,
} from '@/helper/define/legacyEventDefine'
import { TaskStatus } from '@/helper/define/taskDefine'

// Interface
import { WebComponentProps } from '@/helper/registerWebComponent'
import { TaskDetailStyleTypes } from './index'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
const TaskDetailViewWrapper: FC<WebComponentProps> = ({
  customElementName,
  sendDataToCore,
  recvDataFromCore,
  classes,
  ...props
}) => {
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ! State
  const [curTask, setCurTask] = useState<Task>()
  const [curFkStudyId, setCurFkStudyId] = useState<string>()
  const [curProxyToken, setCurProxyToken] = useState<string>()
  const [curLog, setCurLog] = useState<LogProps>()
  const [isConfirmDialogOpened, setConfirmDialogOpened] = useState(false)
  const [confirmDialogInfo, setConfirmDialogInfo] = useState<ConfirmDialogProps>({ title: '', message: '' })
  const [taskLogDialogInfo, setTaskLogDialogInfo] = useState<TaskLogsInfoProps>()
  const [isTaskLogDialogOpened, setTaskLogDialogOpened] = useState(false)
  const [engineNames, setEngineNames] = useState<string[]>([])

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ! I18n
  const dialogTitleViewer = Intl.getIntlText({ id: 'htmljs_react.worklist.taskdetailviewwrapper.dialog_title_log' })
  const dialogMsgCancel = Intl.getIntlText({ id: 'htmljs_react.worklist.taskdetailviewwrapper.dialog_msg_cancel' })
  const dialogTitleCancel = Intl.getIntlText({ id: 'htmljs_react.worklist.taskdetailviewwrapper.dialog_title_cancel' })
  const dialogMsgRestart = Intl.getIntlText({ id: 'htmljs_react.worklist.taskdetailviewwrapper.dialog_msg_restart' })
  const dialogTitleRestart = Intl.getIntlText({ id: 'htmljs_react.worklist.taskdetailviewwrapper.dialog_title_restart' })

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ! Initialize
  const initializeTaskDetail = (data?: { dbId: string; selectedStudyId: number }) => { }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ! Evt handler
  const handleInitTask = () => {
    setCurTask(undefined)
    setCurFkStudyId(undefined)
    setCurProxyToken(undefined)
    setCurLog(undefined)
  }

  const handleSelectTask = async ({ detail: { proxyToken, fkStudyId, task: selectedTask } }: any) => {
    try {
      if (!selectedTask) return

      // ! Clear previous logs
      setCurLog(undefined)

      // ! Set study id
      setCurFkStudyId(fkStudyId)

      // ! Set proxy token
      setCurProxyToken(proxyToken)

      // ! Set selected task info
      setCurTask(selectedTask)

      // ! Get logs
      const {
        data: { logs },
      } = await reqGetLogs({ proxyToken, fkStudyId, fkTaskId: selectedTask.id })
      if (logs.length === 0) return

      // ! Set log
      setCurLog(logs[0])
    } catch (e) { }
  }

  const handleRecievedDataFromCore = ({ dataid, data }: { dataid: string; data: any }) => {
    if (dataid === 'init') initializeTaskDetail(data)
  }

  const handleShowLogBtnClick = async () => {
    if (!curFkStudyId || !curProxyToken) {
      setTaskLogDialogInfo(undefined)
      Logger.errLog('TASK_DETAIL.handleShowLogBtnClick : Not enough info.')
      return
    }

    if (!curLog) {
      setTaskLogDialogInfo(undefined)
      Logger.errLog('TASK_DETAIL.handleShowLogBtnClick : Not logs.')
      return
    }

    const taskLogInfo = await getTaskLogInfo(curLog)
    setTaskLogDialogInfo(taskLogInfo)
    setTaskLogDialogOpened(true)
  }

  const getTaskLogInfo = async ({ dir_name: logDirName, files }: LogProps) => {
    const taskLogsInfoProps: TaskLogsInfoProps = { keys: [], logs: {} }

    for (const { file_name } of files) {
      try {
        const {
          data: { messages },
        } = await reqGetLogByPath({
          proxyToken: curProxyToken!,
          fkStudyId: curFkStudyId!,
          logDirName,
          logFileName: file_name,
        })
        taskLogsInfoProps.keys.push(file_name)
        taskLogsInfoProps.logs[file_name] = {
          messages,
          fileName: file_name,
        }
      } catch (e) {
        Logger.errLog(`TASK_DETAIL.getTaskLogInfo : Fail to get log.(${file_name})`)
      }
    }

    return taskLogsInfoProps
  }

  /**
   * 
   * @param taskId
   * @param force = Admin User의 경우 RUNNING 상태에서도 APS 작업 종료가 가능하도록 구분
   * @returns 
   */
  const handleTaskBtnClick = (type: 'cancel' | 'restart') => (taskId: string, force: boolean = false) => {
    const isCancelTaskDialog = type === 'cancel'
    setConfirmDialogInfo({
      title: isCancelTaskDialog ? dialogTitleCancel : dialogTitleRestart,
      message: isCancelTaskDialog ? dialogMsgCancel : dialogMsgRestart,
      confirm: async () => {
        try {
          let nextStatus: TaskStatus = curTask!.status!
          let updateList = false

          if (isCancelTaskDialog && force) {
            await reqKillTaskByIdWithProcName({ proxyToken: curProxyToken!, taskId, procName: curTask!.proc_name! })

            nextStatus = 'CANCELED'
          } else if (isCancelTaskDialog && !force) {
            await reqCancelTaskById({ proxyToken: curProxyToken!, taskIds: [taskId] })

            // ! WAITING -> CANCELED로 변경.
            nextStatus = 'CANCELED'
          } else {
            // ! RESTART인 경우 현재 TASK를 유지하고 새로 생성.
            await reqRestartTaskById({ proxyToken: curProxyToken!, taskIds: [taskId] })
            updateList = true
          }

          LegacyEvtHandler.dispatchEvt(EVT_UPDATE_CURRENT_TASK_STATUS, {
            taskId: curTask!.id!,
            nextStatus,
            updateList,
          })

          if (nextStatus !== curTask!.status!) {
            setCurTask({
              ...curTask!,
              status: nextStatus,
            })
          }

          // !USER_TASK는 선택된 Task를 복사해서 새로운 USER_TASK로 생성
          if (updateList && curTask!.type! === 'APS_RULE_TASK') {
            setCurTask(undefined)
          }
        } catch (e) {
          Logger.errLog('TASK_DETAIL.handleTaskBtnClick : Failed')
          console.log(e)
        }

        setConfirmDialogOpened(false)
      },
    })

    setConfirmDialogOpened(true)
  }

  const handleConfirmDialogCloseBtnClick = () => setConfirmDialogOpened(false)
  const handleLogDialogCloseBtnClick = () => setTaskLogDialogOpened(false)

  const handleDownloadFile = async () => {
    const url = await getURLforDownloadTaskLogs({ proxyToken: curProxyToken!, studyId: curFkStudyId!, dirName: curLog!.dir_name })
    downloadFile(
      url,
      `${curLog!.name}.zip`
    )
  }

  const checkEngineValid = useCallback((engineName: string) => {
    return engineNames.includes(engineName)
  }, [engineNames])

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ! Effect
  useEffect(() => {
    let mounted = true
    LegacyEvtHandler.addEvtListener(EVT_TASKLIST_INIT, handleInitTask)
    LegacyEvtHandler.addEvtListener(EVT_TASKLIST_SELECTION, handleSelectTask)

    const initEngines = async () => {
      try {
        const { data: { engines } } = await reqGetAPSEngines()
        if (mounted) setEngineNames([...engines.map(({ name }) => name)])
      } catch (e) {
        Logger.errLog('TASK_DETAIL.mounted : Failed to get aps engines.')
      }
    }

    initEngines()

    return () => {
      mounted = false
      LegacyEvtHandler.removeEvtListener(EVT_TASKLIST_INIT, handleInitTask)
      LegacyEvtHandler.removeEvtListener(EVT_TASKLIST_SELECTION, handleSelectTask)
    }

    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (!recvDataFromCore) return
    handleRecievedDataFromCore({ ...recvDataFromCore })

    // eslint-disable-next-line
  }, [recvDataFromCore])

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ! Render
  return (
    <>
      <TaskDetailView
        task={curTask || undefined}
        onShowLogBtnClick={curLog ? handleShowLogBtnClick : undefined}
        onCancelTaskBtnClick={handleTaskBtnClick('cancel')}
        onRestartTaskBtnClick={handleTaskBtnClick('restart')}
        classes={classes as TaskDetailStyleTypes}
        checkEngineValid={checkEngineValid}
      />
      <TaskLogDialog
        title={dialogTitleViewer}
        taskLogsInfo={taskLogDialogInfo}
        downloadFile={handleDownloadFile}
        onCloseBtnClick={handleLogDialogCloseBtnClick}
        open={isTaskLogDialogOpened}
      />
      <ConfirmDialog
        open={isConfirmDialogOpened}
        title={confirmDialogInfo.title}
        messages={confirmDialogInfo.message}
        onConfirmBtnClick={confirmDialogInfo.confirm}
        onCloseBtnClick={handleConfirmDialogCloseBtnClick}
        hasCancelBtn={true}
      ></ConfirmDialog>
    </>
  )
}

export default TaskDetailViewWrapper
